import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { Field } from '@/shared/components/Field';
import { MoneyInput } from '@/shared/components/MoneyInput';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { useToast } from '@/shared/components/Toast';
import { AppError } from '@/core/errors/AppError';
import { formatearMonto } from '@/core/utils/formatters';
import { RegistrarPagoSchema } from '@/data/dto/pago';
import { METODOS_PAGO } from '@/data/dto/reserva';
import { useRegistrarPago } from '@/data/hooks/usePagos';
import { usePuntosVenta } from '@/data/hooks/useCatalogos';
export function RegistrarPagoModal({ open, onOpenChange, reservaId, sucursalId, saldoPendiente, metodoSugerido, }) {
    const toast = useToast();
    const { data: pdvs, isLoading: cargandoPdvs } = usePuntosVenta(sucursalId);
    const registrar = useRegistrarPago(reservaId);
    const form = useForm({
        resolver: zodResolver(RegistrarPagoSchema),
        defaultValues: {
            metodo: (metodoSugerido && METODOS_PAGO.includes(metodoSugerido)
                ? metodoSugerido
                : 'EFECTIVO'),
            monto: saldoPendiente && saldoPendiente > 0 ? saldoPendiente.toFixed(2) : '',
            pdvId: '',
            observaciones: '',
        },
    });
    useEffect(() => {
        if (!open)
            return;
        form.reset({
            metodo: (metodoSugerido && METODOS_PAGO.includes(metodoSugerido)
                ? metodoSugerido
                : 'EFECTIVO'),
            monto: saldoPendiente && saldoPendiente > 0 ? saldoPendiente.toFixed(2) : '',
            pdvId: pdvs?.[0]?.id ?? '',
            observaciones: '',
        });
    }, [open, saldoPendiente, metodoSugerido, pdvs, form]);
    const errors = form.formState.errors;
    const onSubmit = form.handleSubmit((values) => {
        registrar.mutate(values, {
            onSuccess: () => {
                toast.exito('Pago registrado');
                onOpenChange(false);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos registrar el pago.'),
        });
    });
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "sm", children: [_jsx(ModalHeader, { titulo: "Registrar pago", descripcion: saldoPendiente != null
                        ? `Saldo pendiente: ${formatearMonto(saldoPendiente)}`
                        : undefined }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsx(ModalBody, { children: _jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(Field, { label: "Monto", htmlFor: "monto", error: errors.monto?.message, required: true, children: _jsx(MoneyInput, { id: "monto", autoFocus: true, error: Boolean(errors.monto), value: form.watch('monto') ?? '', onChange: (v) => form.setValue('monto', v, { shouldDirty: true, shouldValidate: true }) }) }), _jsx(Field, { label: "M\u00E9todo de pago", htmlFor: "metodo", error: errors.metodo?.message, required: true, children: _jsx(Select, { id: "metodo", ...form.register('metodo'), children: METODOS_PAGO.map((m) => (_jsx("option", { value: m, children: m }, m))) }) }), _jsx(Field, { label: "Punto de venta", htmlFor: "pdvId", error: errors.pdvId?.message, required: true, helper: cargandoPdvs
                                            ? 'Cargando…'
                                            : pdvs && pdvs.length === 0
                                                ? 'Esta sucursal no tiene puntos de venta activos. Pedile a un admin que cargue uno.'
                                                : undefined, children: _jsxs(Select, { id: "pdvId", ...form.register('pdvId'), disabled: cargandoPdvs, children: [_jsx("option", { value: "", children: "Seleccion\u00E1\u2026" }), pdvs?.map((p) => (_jsx("option", { value: p.id, children: p.nombre }, p.id)))] }) }), _jsx(Field, { label: "Observaciones", htmlFor: "observaciones", error: errors.observaciones?.message, children: _jsx(Textarea, { id: "observaciones", rows: 2, ...form.register('observaciones') }) })] }) }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: registrar.isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: registrar.isPending, children: "Confirmar pago" })] })] })] }) }));
}
